import React, { useState } from 'react'
import { IconHistory, IconCheck, IconChevronRight } from '@tabler/icons-react'
import { formatDistanceToNow } from 'date-fns'
import { toString } from 'lodash'
import classNames from 'classnames'

import { ExternalLink } from '@/components/ExternalLink'
import { CopyButton } from '@/components/buttons/CopyButton'
import { Pill } from '@/components/pills/Pill'
import {
  getEvidenceCallId,
  getEvidenceLink,
  getDisplayLabel,
  isPassResult,
  isFailResult,
} from '@/utils/promptFeedback.helpers'

import { PromptDiffModal } from './PromptDiffModal'

function resolveUserName(evidenceItem, fallbackAgentName) {
  const { created_by_first_name: first, created_by_last_name: last } = evidenceItem
  return first && last ? `${first} ${last}` : fallbackAgentName || 'Agent'
}

/**
 * @param {boolean} showPromptStatus - Whether to show prompt version status badges (default: true)
 * @param {string} fallbackAgentName - Fallback agent name if not available on evidence item
 */
export const EvidenceList = ({
  allEvidence,
  expectedScore,
  feedbackText,
  prompts = [],
  showPromptStatus = true,
  fallbackAgentName = null,
}) => {
  const [diffModalEvidenceId, setDiffModalEvidenceId] = useState(null)
  const [expandedCards, setExpandedCards] = useState(() =>
    allEvidence.reduce((acc, item) => ({ ...acc, [item.id]: true }), {})
  )

  const toggleCardExpanded = (evidenceId) => {
    setExpandedCards((prev) => ({ ...prev, [evidenceId]: !prev[evidenceId] }))
  }

  return (
    <div className="feedback-reports-list">
      {allEvidence.map((evidenceItem) => {
        const callId = getEvidenceCallId(evidenceItem)
        const callLink = getEvidenceLink(evidenceItem)
        const evidenceExpectedResult = evidenceItem.expected_result || expectedScore
        const evidenceUserFeedback = evidenceItem.user_feedback || feedbackText

        const userName = resolveUserName(evidenceItem, fallbackAgentName)

        const evidencePromptId = evidenceItem.prompt_id
        const evidenceCurrentPromptId = evidenceItem.current_prompt_id
        const isEvidenceOutdated =
          evidencePromptId &&
          evidenceCurrentPromptId &&
          evidencePromptId !== evidenceCurrentPromptId

        const oldPrompt = prompts.find((prompt) => prompt.id === evidencePromptId)
        const currentPrompt = prompts.find((prompt) => prompt.id === evidenceCurrentPromptId)
        const isExpanded = expandedCards[evidenceItem.id]

        return (
          <div key={evidenceItem.id} className="feedback-report-item">
            <div className="feedback-report-header">
              {evidenceExpectedResult && (
                <div
                  className="result-comparison-pills clickable"
                  onClick={() => toggleCardExpanded(evidenceItem.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      toggleCardExpanded(evidenceItem.id)
                    }
                  }}
                >
                  <IconChevronRight
                    size={14}
                    className={classNames('evidence-chevron', { expanded: isExpanded })}
                  />
                  <span className="result-label">User expected:</span>
                  <Pill
                    xs
                    success={isPassResult(evidenceExpectedResult)}
                    critical={isFailResult(evidenceExpectedResult)}
                  >
                    {getDisplayLabel(evidenceExpectedResult)}
                  </Pill>
                </div>
              )}

              <span className="report-call-id">
                <ExternalLink url={callLink} label={callId} />
                <CopyButton content={toString(callId)} showTooltip iconOnly />
              </span>
            </div>

            <div className={classNames('evidence-expandable-content', { expanded: isExpanded })}>
              <div className="evidence-content-inner">
                {evidenceUserFeedback && (
                  <div className="user-feedback-section">
                    <div className="user-feedback-header-row">
                      <span className="user-name">{userName}</span>
                    </div>
                    <div className="user-feedback-text">{evidenceUserFeedback}</div>
                  </div>
                )}

                {evidenceItem.ai_explanation && (
                  <div className="evidence-reasoning-section">
                    <div className="evidence-reasoning-header">
                      <span className="reasoning-name">Reasoning</span>
                    </div>
                    <div className="evidence-reasoning-text">{evidenceItem.ai_explanation}</div>
                  </div>
                )}

                <div className="feedback-report-footer">
                  {evidenceItem.created_at && (
                    <span className="footer-timestamp">
                      Reported {formatDistanceToNow(new Date(evidenceItem.created_at))} ago
                    </span>
                  )}
                  {showPromptStatus && evidencePromptId && evidenceCurrentPromptId && (
                    <>
                      {isEvidenceOutdated ? (
                        <>
                          <Pill
                            xs
                            outlined
                            caution
                            clickable
                            onClick={() => setDiffModalEvidenceId(evidenceItem.id)}
                          >
                            <IconHistory /> Prompt updated since feedback
                          </Pill>
                          <PromptDiffModal
                            open={diffModalEvidenceId === evidenceItem.id}
                            onClose={() => setDiffModalEvidenceId(null)}
                            oldPrompt={oldPrompt}
                            newPrompt={currentPrompt}
                          />
                        </>
                      ) : (
                        <Pill xs outlined success>
                          <IconCheck size={12} /> Prompt is current
                        </Pill>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
